import axios from 'axios'
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import 'firebase/compat/firestore'
import 'firebase/compat/installations'
import 'firebase/compat/storage'
import { getMessaging, getToken, onMessage } from 'firebase/messaging'
import { getRemoteConfig, getValue } from 'firebase/remote-config'
import { useEffect } from 'react'

import firebaseWebConfig from '../firebase-web-config.json'
import { getParams } from '../hooks'
import {
  Config,
  generateFirestoreId,
  initCloseStripeButton,
  readableError,
  showStripe,
  VariableSourceType,
} from '../utils'
import { FD, FF, FirebaseContext, FP, FPVT, FV } from './index'

export interface FirebaseConfig {
  [key: string]: string
}

interface Props {
  children: React.ReactNode
  config: Config
  webFirebaseConfig?: FirebaseConfig
  firestorePrefix?: string
}

export const FirebaseProvider: React.FC<Props> = ({ children, config, webFirebaseConfig, firestorePrefix = '' }) => {
  const emailKey = 'email'

  const firebaseConfig = webFirebaseConfig || (firebaseWebConfig as FirebaseConfig)

  const app = firebase.initializeApp(firebaseConfig)

  const auth = firebase.auth()

  const firestore = firebase.firestore(app)

  const storage = firebase.storage(app)

  const remoteConfig = getRemoteConfig(app)
  remoteConfig.settings.minimumFetchIntervalMillis = 0

  const messaging = getMessaging(app)

  onMessage(messaging, payload => {
    const { title = '', body, icon } = payload.notification || {}
    new Notification(title, { body, icon })
  })

  const googleProvider = new firebase.auth.GoogleAuthProvider()
  googleProvider.setCustomParameters({ prompt: 'select_account' })

  const signInWithGoogle = async () => {
    await auth.signInWithPopup(googleProvider)
  }

  const createUserWithEmailAndPassword: FP = async (...props) => {
    const { email, password } = await getParams(...props)
    await auth.createUserWithEmailAndPassword(email || '', password || '')
  }

  const signInWithEmailAndPassword: FP = async (...props) => {
    const { email, password } = await getParams(...props)
    await auth.signInWithEmailAndPassword(email || '', password || '')
  }

  const sendPasswordResetEmail: FV = async (getVariableValue, variable) => {
    const email = (await getVariableValue(variable)) || ''
    await auth.sendPasswordResetEmail(email).then(() => alert(`Link was sent to ${email}`))
  }

  const sendSignInLinkToEmail: FV = async (getVariableValue, variable) => {
    const email = (await getVariableValue(variable)) || ''
    await auth
      .sendSignInLinkToEmail(email, { url: window.location.origin, handleCodeInApp: true })
      .then(() => localStorage.setItem(emailKey, email))
  }

  const reSendSignInLinkToEmail: FV = async (...props) => {
    const result = window.confirm('Trouble getting email? Resend?')
    if (result) {
      await sendSignInLinkToEmail(...props)
    }
  }

  const httpClient = axios.create({ headers: { 'Content-Type': 'application/json' } })

  httpClient.interceptors.request.use(
    async config => {
      const accessToken = await auth.currentUser?.getIdToken()
      if (accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`
      }
      return config
    },
    err => Promise.reject(err)
  )

  httpClient.interceptors.response.use(
    res => res,
    async err => {
      if (err.response) {
        const originalConfig = err.config
        if (err.response.status === 401 && !originalConfig._retry) {
          originalConfig._retry = true
          const accessToken = await auth.currentUser?.getIdToken(true)
          if (accessToken) {
            httpClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
          }
          return httpClient(originalConfig)
        }
      }
      return Promise.reject(err)
    }
  )

  const getApiUrl = () => {
    const errorMessage = 'Backend is not deployed.'
    const { apiUrl } = config.data.remoteConfig || {}
    if (!apiUrl) {
      throw new Error(errorMessage)
    }
    const value = getRemoteConfigValue(apiUrl)
    if (!value) {
      throw new Error(errorMessage)
    }
    return value
  }

  const getRemoteConfigValue = (key: string) => getValue(remoteConfig, key).asString()

  const getDeviceId = () => app.installations().getId()

  const getNotificationToken = () => getToken(messaging, { vapidKey: firebaseConfig.vapidKey })

  const setNotificationToken = async () => {
    const deviceId = await getDeviceId()
    const token = await getNotificationToken()
    const { onDeviceRegister } = config.authentication.triggers
    await runFlow(onDeviceRegister, {
      id: deviceId,
      fcmToken: token,
      platform: 'web',
      name: window.navigator.userAgent,
    })
  }

  const deleteNotificationToken = async () => {
    const deviceId = await getDeviceId()
    const { onDeviceUnregister } = config.authentication.triggers
    await runFlow(onDeviceUnregister, { deviceId })
  }

  const checkNotificationsEnabled = async () => {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        await setNotificationToken()
        return true
      } catch {
        return false
      }
    } else {
      return false
    }
  }

  const enableNotifications = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission()
      if (permission === 'granted') {
        await setNotificationToken()
        return
      }
    }
    throw new Error('User Notifications Permission Denied. Enable Notifications in browser settings.')
  }

  const logout = async () => {
    try {
      await deleteNotificationToken()
    } catch {}
    await auth.signOut()
  }

  const runFlow: FD = async (name, data) => {
    const apiUrl = getApiUrl()
    return httpClient.post(`${apiUrl}/v1/flows/${name}`, data)
  }

  const search = (url: string) => {
    const apiUrl = getApiUrl()
    return httpClient.get(`${apiUrl}/v1/${url}`)
  }

  const oauth: FPVT = (getVariableValue, parameters, valueTarget) =>
    new Promise(async (resolve, reject) => {
      const { provider, scopes } = await getParams(getVariableValue, parameters)
      if (provider === 'google' && Array.isArray(scopes)) {
        const errorMessage = 'No oAuthClientId.'
        const { oAuthClientId } = config.data.remoteConfig || {}
        if (!oAuthClientId) {
          throw new Error(errorMessage)
        }
        const client_id = getRemoteConfigValue(oAuthClientId)
        if (!client_id) {
          throw new Error(errorMessage)
        }
        const codeClient = google.accounts.oauth2.initCodeClient({
          client_id,
          scope: scopes.join(' '),
          ux_mode: 'popup',
          callback: async ({ code }) => {
            const value = JSON.stringify({ client_id, redirect_uri: 'postmessage', scopes, code })
            await getVariableValue({ source: valueTarget }, undefined, { value })
            resolve()
          },
          error_callback: err => reject(err),
        })
        codeClient.requestCode()
      } else {
        reject(new Error('Something went wrong!'))
      }
    })

  const checkoutSelected = (priceId: string) =>
    new Promise(async (resolve, reject) => {
      const { onStripeCheckout, onStripeCheckoutEnded } = config.billing?.triggers || {}
      if (onStripeCheckout && onStripeCheckoutEnded) {
        try {
          const {
            data: { id: checkoutSessionId, clientSecret },
          } = await runFlow(onStripeCheckout, { priceId })
          // @ts-ignore
          const stripe = Stripe(getRemoteConfigValue('stripePublishableKey'))
          const checkout = await stripe.initEmbeddedCheckout({
            clientSecret,
            onComplete: () =>
              runFlow(onStripeCheckoutEnded, { checkoutSessionId }).finally(() => closeStripeButton.click()),
          })
          showStripe()
          checkout.mount('#stripe')
          const closeStripeButton = initCloseStripeButton(() => {
            checkout.destroy()
            resolve(null)
          })
        } catch (err: any) {
          reject(err)
        }
      } else {
        reject(new Error('Something went wrong!'))
      }
    })

  const getAssetRecord: FF = (getVariableValue, file) =>
    new Promise(async resolve => {
      const currentUserId = await getVariableValue({
        source: { type: VariableSourceType.globalVariable, variableName: 'currentUserId' },
      })
      const fileRef = storage.ref(`uploads/${currentUserId}/${generateFirestoreId()}/${file.name}`)
      fileRef
        .put(file)
        .then(() =>
          fileRef.getDownloadURL().then(async url => {
            const { onMetadataExtract } = config.assets.triggers
            const {
              data: { bytes, resourceType, mimeType, format, width, height, duration, pageCount },
            } = await runFlow(onMetadataExtract, { url })
            resolve({
              bytes,
              duration,
              error: null,
              folder: '~',
              format,
              height,
              originalFileUri: fileRef.toString(),
              pageCount,
              resourceType,
              signature: null,
              status: null,
              thumbnailUrl: null,
              title: file.name,
              url,
              version: null,
              width,
              mimeType,
              startTime: null,
              ownerId: currentUserId,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            })
          })
        )
        .catch(err => alert(readableError(err.message)))
        .finally(() => resolve(null))
    })

  useEffect(() => {
    const googleMaps = document.getElementById('googleMaps') as HTMLScriptElement
    googleMaps.setAttribute(
      'src',
      `https://maps.googleapis.com/maps/api/js?key=${firebaseConfig.apiKey}&loading=async&callback=Function.prototype`
    )
  }, [])

  return (
    <FirebaseContext.Provider
      value={{
        f: {
          firestorePrefix,
          auth,
          firestore,
          remoteConfig,
          getRemoteConfigValue,
          signInWithGoogle,
          createUserWithEmailAndPassword,
          signInWithEmailAndPassword,
          sendPasswordResetEmail,
          sendSignInLinkToEmail,
          reSendSignInLinkToEmail,
          checkNotificationsEnabled,
          enableNotifications,
          logout,
          runFlow,
          search,
          oauth,
          checkoutSelected,
          getAssetRecord,
          emailKey,
        },
      }}
    >
      {children}
    </FirebaseContext.Provider>
  )
}
